import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';

// ============================================================================
// ObjectCommandBar — компактная полоса СТАНДАРТНЫХ КОМАНД объекта (task/order)
// над контентом формы. Кнопки берутся из SSoT-сервера (ListStandardCommands),
// клиент НЕ хардкодит набор. Исключаются команды, дублирующие кнопки шапки
// окна (Записать/Записать и закрыть/Закрыть/Обновить). Для заявок/задач
// набор даёт «Выполнено»/«Отменить»/«Удалить» — обработка в runCommand формы.
// ============================================================================

const log = createLogger('ObjectCommandBar');

// Команды шапки окна — не дублируем в полосе формы. «Удалить окончательно»,
// «Восстановить» и «Передать» (диалог выбора исполнителя) живут в меню «Ещё».
// «Печать» — отдельная кнопка шапки (PrintFormButton: сама решает, есть ли
// печатные формы, и открывает диалог выбора) — в полосе не дублируем.
const EXCLUDED = new Set(['save', 'save_and_close', 'close', 'refresh', 'post', 'post_and_close', 'delete_permanent', 'restore', 'print', 'transfer']);

interface ObjectCommandBarProps {
  entityType: string;
  objectId?: string;
  disabled?: boolean;
  /** Коды команд, скрываемых на КЛИЕНТЕ (UX-скрытие; сервер всё равно защищает):
   *  напр. ['take'] — когда задача уже назначена исполнителю, ['delete'] — когда
   *  пользователь не админ и не создатель записи. */
  hideCodes?: string[];
  onCommand: (command: string) => void;
}

export function ObjectCommandBar({ entityType, objectId, disabled, hideCodes, onCommand }: ObjectCommandBarProps) {
  const [commands, setCommands] = useState<Array<{ code: string; synonym: string }>>([]);
  const hideKey = (hideCodes || []).join(',');

  useEffect(() => {
    const supported = entityType === 'task' || entityType === 'order';
    if (!objectId || !supported) {
      setCommands([]);
      return;
    }
    let cancelled = false;
    const hidden = new Set((hideKey ? hideKey.split(',') : []).filter(Boolean));
    getSDK().metadata.listStandardCommands({ entityType, formType: 'object' } as any)
      .then((r: any) => {
        if (cancelled) return;
        const raw = (r?.items || []) as Array<{ code: string; synonym: string; kind: string }>;
        const items = raw
          .filter((c) => c.kind !== 'list' && !EXCLUDED.has(c.code) && !hidden.has(c.code))
          .map((c) => ({ code: c.code, synonym: c.synonym }));
        setCommands(items);
        // ДИАГНОСТИКА (журнал, ui): что вернул сервер и что скрыто — разбор «кнопки нет».
        void import('@/lib/eventLog').then((m) => m.eventLog.info('ui',
          `object-commands: ${entityType}`,
          { details: JSON.stringify({ objectId, hideCodes, got: raw.map((c) => c.code), shown: items.map((c) => c.code) }) }));
      })
      .catch((e: any) => {
        log.error('listStandardCommands failed', { entityType, error: e?.message || String(e) });
        if (!cancelled) setCommands([]);
      });
    return () => { cancelled = true; };
  }, [entityType, objectId, hideKey]);

  if (commands.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {commands.map((c) => (
        <Button
          key={c.code}
          size="sm"
          variant={c.code === 'delete' ? 'destructive' : c.code === 'done' ? 'default' : 'outline'}
          disabled={disabled}
          onClick={() => onCommand(c.code)}
        >
          {c.synonym || c.code}
        </Button>
      ))}
    </div>
  );
}
